import { Member } from 'state/reducers/patient';

/** Query param that keeps the review list clinic filter across reloads, links and tab changes */
export const REVIEW_LIST_CLINIC_PARAM = 'clinic';

export type ClinicOption = {
  clinicId: number;
  name: string;
  count: number;
};

export const getClinicFilterFromSearch = (search: string): number | null => {
  const value = new URLSearchParams(search).get(REVIEW_LIST_CLINIC_PARAM);
  const clinicId = Number(value);

  return value && Number.isInteger(clinicId) && clinicId > 0 ? clinicId : null;
};

/** Returns `search` with the clinic param set (or removed when `clinicId` is null) */
export const setClinicFilterInSearch = (search: string, clinicId: number | null): string => {
  const params = new URLSearchParams(search);

  if (clinicId) {
    params.set(REVIEW_LIST_CLINIC_PARAM, String(clinicId));
  } else {
    params.delete(REVIEW_LIST_CLINIC_PARAM);
  }

  const updatedSearch = params.toString();
  return updatedSearch ? `?${updatedSearch}` : '';
};

/** Keeps only the clinic param, so path rewrites (e.g. tab changes) don't drop the filter */
export const keepClinicFilterSearch = (search: string): string =>
  setClinicFilterInSearch('', getClinicFilterFromSearch(search));

/**
 * One option per clinic present in the list, sorted by name. Names come from the
 * payload (`clinic_name`); RL2 lists don't send it, so `fallbackNames` fills the gap.
 */
export const buildClinicOptions = (
  members: Member[],
  fallbackNames: Record<number, string> = {}
): ClinicOption[] => {
  const options = new Map<number, ClinicOption>();

  members.forEach(({ clinic_id: clinicId, clinic_name: clinicName }) => {
    if (!clinicId) {
      return;
    }

    const option = options.get(clinicId);
    if (option) {
      option.count += 1;
      return;
    }

    options.set(clinicId, {
      clinicId,
      name: clinicName || fallbackNames[clinicId] || `Clinic #${clinicId}`,
      count: 1,
    });
  });

  return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name));
};
